import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Dropzone } from "@/components/ui/customs/dropzone-file";
import { Modal } from "@/components/ui/customs/modal";
import { useS3Upload } from "@/hooks/useS3Upload";
import { SubmitHandler, useForm } from "react-hook-form";
import {
  AdjustmentInvoiceFormData,
  AdjustmentInvoiceSchema,
  EditInvoiceFormData,
  EditInvoiceSchema,
} from "@/types/forms/bo/invoiceForm.form";
import { zodResolver } from "@hookform/resolvers/zod";
import { ControlledInput } from "@/components/ui/customs/controlled-input";
import { useUpdateInvoice } from "@/api/invoice";
import { InvoiceDto } from "@/types/api/api-types";
import { InvoiceStatusEnum } from "@/constants/enums/invoiceStatus.enum";
import { FileCog, Loader2, Pen, TriangleAlert } from "lucide-react";
import { ControlledUnifiedInput } from "@/components/ui/customs/controlled-unified-input";
import { useTranslation } from "react-i18next";

export type InvoiceEditModalProps = {
  closeModal: () => void;
  invoice: InvoiceDto;
};

export const InvoiceEditModal = ({ closeModal, invoice }: InvoiceEditModalProps) => {
  const { t } = useTranslation(["boInvoices", "common"]);
  const [showAjustement, setShowAjustement] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [error, setError] = useState<string>("");
  const [excelFiles, setExcelFiles] = useState<File[] | null>(null);
  const [pdfFiles, setPdfFiles] = useState<File[] | null>(null);
  const [isUploading, setIsUploading] = useState(false);

  const { uploadFiles } = useS3Upload();
  const { mutateAsync: updateInvoice, isPending } = useUpdateInvoice();

  const onValidateAjustement = async (data: AdjustmentInvoiceFormData) => {
    const res = await updateInvoice({
      id: invoice.id,
      data: {
        adjustment: data.adjustment * 100,
      },
    });
    if (res.status === 200) {
      setShowConfirmationModal(false);
      closeModal();
    }
  };

  const onValidateEdit = async (data: EditInvoiceFormData) => {
    const res = await updateInvoice({
      id: invoice.id,
      data: {
        ...data,
        amountToBill: data.amountToBill * 100,
        totalHT: data.totalHT * 100,
        totalTva: data.totalTva * 100,
      },
    });
    setIsUploading(false);
    if (res.status === 200) {
      setShowConfirmationModal(false);
      closeModal();
    }
  };

  const invoiceForm = useForm<EditInvoiceFormData>({
    defaultValues: {
      amountToBill: 0,
      totalHT: 0,
      totalTva: 0,
      invoiceKey: undefined,
      invoicePDFKey: undefined,
    },
    resolver: zodResolver(EditInvoiceSchema),
    mode: "onChange",
    reValidateMode: "onChange",
  });

  const totalHtWatch = invoiceForm.watch("totalHT");
  const totalTvaWatch = invoiceForm.watch("totalTva");

  useEffect(() => {
    invoiceForm.setValue("amountToBill", (totalHtWatch || 0) + (totalTvaWatch || 0));
  }, [totalHtWatch, totalTvaWatch, invoiceForm]);

  const handleUploadFiles = async () => {
    if (!excelFiles || excelFiles.length === 0 || !pdfFiles || pdfFiles.length === 0) return;

    setIsUploading(true);
    try {
      const [excelKeys, pdfKeys] = await Promise.all([
        uploadFiles(excelFiles, {
          isPrivate: false,
          s3Prefix: "pelican-invoices",
        }),
        uploadFiles(pdfFiles, {
          isPrivate: false,
          s3Prefix: "pelican-invoices",
        }),
      ]);
      if (excelKeys.length === 0 || pdfKeys.length === 0) {
        throw new Error("Invoice files upload failed");
      }
      invoiceForm.setValue("invoiceKey", excelKeys[0]);
      invoiceForm.setValue("invoicePDFKey", pdfKeys[0]);
      invoiceForm.handleSubmit(handleValidateEdit)();
    } catch {
      setError(t("editModal.uploadError"));
      setIsUploading(false);
    }
  };

  const adjustmentForm = useForm<AdjustmentInvoiceFormData>({
    defaultValues: {
      adjustment: 0,
    },
    resolver: zodResolver(AdjustmentInvoiceSchema),
    mode: "onSubmit",
    reValidateMode: "onChange",
  });

  useEffect(() => {
    adjustmentForm.setValue("adjustment", 0);
  }, [showAjustement, adjustmentForm]);

  useEffect(() => {
    setExcelFiles(null);
    setPdfFiles(null);
    invoiceForm.setValue("invoiceKey", "");
    invoiceForm.setValue("invoicePDFKey", "");
    invoiceForm.setValue("totalHT", 0);
    invoiceForm.setValue("totalTva", 0);
    invoiceForm.setValue("amountToBill", 0);
  }, [showEdit, invoiceForm]);

  const handleValidateAjustement: SubmitHandler<AdjustmentInvoiceFormData> = (
    data: AdjustmentInvoiceFormData,
  ) => {
    if (onValidateAjustement) onValidateAjustement(data);
  };

  const handleConfirmAdjustment = () => {
    if (showAjustement) {
      adjustmentForm.handleSubmit(handleValidateAjustement)();
    } else {
      handleUploadFiles();
    }
  };

  const handleValidateEdit: SubmitHandler<EditInvoiceFormData> = (data: EditInvoiceFormData) => {
    if (onValidateEdit) onValidateEdit(data);
  };

  const modalTitle = showEdit ? (
    <div className="flex items-center gap-3">
      <div className="flex size-10 items-center justify-center rounded-full bg-blue-100">
        <Pen className="size-5 text-blue-600" />
      </div>
      <span>{t("editModal.editTitle")}</span>
    </div>
  ) : showAjustement ? (
    <div className="flex items-center gap-3">
      <div className="flex size-10 items-center justify-center rounded-full bg-amber-100">
        <FileCog className="size-5 text-amber-600" />
      </div>
      <span>{t("editModal.adjustmentTitle")}</span>
    </div>
  ) : (
    <div className="flex items-center gap-3">
      <div className="flex size-10 items-center justify-center rounded-full bg-blue-100">
        <Pen className="size-5 text-blue-600" />
      </div>
      <span>{t("editModal.choiceTitle")}</span>
    </div>
  );

  return (
    <>
      <Modal
        open={true}
        onClose={closeModal}
        dismissible
        size="sm"
        title={modalTitle}
        footer={
          <Button
            variant="ghost"
            onClick={closeModal}
            className="w-full"
            disabled={isPending || isUploading}
          >
            {t("common:actions.cancel")}
          </Button>
        }
      >
        {!showAjustement && !showEdit && (
          <div className="flex flex-col gap-4">
            {invoice.type === "AUTO" && (
              <Button className="w-full" onClick={() => setShowAjustement(true)}>
                {t("editModal.makeAdjustment")}
              </Button>
            )}
            <Button className="w-full" variant="secondary" onClick={() => setShowEdit(true)}>
              {t("editModal.editInvoice")}
            </Button>
          </div>
        )}
        {showAjustement && (
          <form
            className="flex flex-col gap-4"
            onSubmit={adjustmentForm.handleSubmit(handleValidateAjustement)}
          >
            <ControlledInput
              control={adjustmentForm.control}
              name="adjustment"
              placeholder={t("editModal.adjustmentPlaceholder")}
              label={t("editModal.adjustmentLabel")}
              inputType="float"
            />
            {invoice.status === InvoiceStatusEnum.VALIDATED ? (
              <Button
                className="w-full"
                type="button"
                onClick={() => setShowConfirmationModal(true)}
                disabled={isPending}
              >
                {isPending ? (
                  <Loader2 className="animate-spin" />
                ) : (
                  t("editModal.validateAdjustment")
                )}
              </Button>
            ) : (
              <Button className="w-full" type="submit" disabled={isPending}>
                {isPending ? (
                  <Loader2 className="animate-spin" />
                ) : (
                  t("editModal.validateAdjustment")
                )}
              </Button>
            )}
            <Button variant="ghost" onClick={() => setShowAjustement(false)} className="w-full">
              {t("common:actions.back")}
            </Button>
          </form>
        )}

        {showEdit && (
          <div className="flex flex-col gap-4">
            <div className="grid w-full grid-cols-3 items-center justify-between gap-4">
              <ControlledUnifiedInput
                control={invoiceForm.control}
                inputType="float"
                name="totalHT"
                placeholder={t("editModal.totalHtPlaceholder")}
                label={t("editModal.totalHtLabel")}
              />
              <ControlledUnifiedInput
                control={invoiceForm.control}
                inputType="float"
                name="totalTva"
                placeholder={t("editModal.totalTvaPlaceholder")}
                label={t("editModal.totalTvaLabel")}
              />
              <ControlledUnifiedInput
                control={invoiceForm.control}
                inputType="float"
                name="amountToBill"
                placeholder={t("editModal.totalPlaceholder")}
                label={t("editModal.totalLabel")}
                disabled={true}
              />
            </div>
            <label className="font-medium">{t("editModal.excelFileLabel")}</label>
            <Dropzone
              files={excelFiles}
              setFiles={setExcelFiles}
              maxFiles={1}
              accept={{
                "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [".xlsx"],
                "application/vnd.ms-excel": [".xls"],
              }}
              dropZoneText={t("editModal.excelFileDropzone")}
            />
            <label className="font-medium">{t("editModal.pdfFileLabel")}</label>
            <Dropzone
              files={pdfFiles}
              setFiles={setPdfFiles}
              maxFiles={1}
              accept={{ "application/pdf": [".pdf"] }}
              dropZoneText={t("editModal.pdfFileDropzone")}
            />
            {error && <span className="text-sm text-red-500">{error}</span>}
            {invoice.status === InvoiceStatusEnum.VALIDATED ? (
              <Button
                className="w-full"
                type="button"
                onClick={() => setShowConfirmationModal(true)}
                disabled={
                  isUploading ||
                  !excelFiles ||
                  excelFiles.length === 0 ||
                  !pdfFiles ||
                  pdfFiles.length === 0
                }
              >
                {t("editModal.submit")}
              </Button>
            ) : (
              <Button
                className="w-full"
                onClick={handleUploadFiles}
                disabled={
                  isUploading ||
                  !excelFiles ||
                  excelFiles.length === 0 ||
                  !pdfFiles ||
                  pdfFiles.length === 0
                }
              >
                {isUploading ? <Loader2 className="animate-spin" /> : t("editModal.submit")}
              </Button>
            )}
            <Button
              variant="ghost"
              onClick={() => setShowEdit(false)}
              className="w-full"
              disabled={isUploading}
            >
              {t("common:actions.back")}
            </Button>
          </div>
        )}
      </Modal>

      <Modal
        open={showConfirmationModal}
        onClose={() => setShowConfirmationModal(false)}
        dismissible
        size="sm"
        title={
          <div className="flex items-center gap-3">
            <div className="flex size-10 items-center justify-center rounded-full bg-amber-100">
              <TriangleAlert className="size-5 text-amber-600" />
            </div>
            <span>{t("editConfirmModal.title")}</span>
          </div>
        }
        description={t("editConfirmModal.description")}
        footer={
          <div className="flex w-full flex-col gap-2 md:flex-row">
            <Button
              onClick={handleConfirmAdjustment}
              disabled={isPending || isUploading}
              className="flex-1"
            >
              {isPending || isUploading ? (
                <Loader2 className="animate-spin" />
              ) : (
                t("common:actions.confirm")
              )}
            </Button>
            <Button
              variant="outline"
              onClick={() => setShowConfirmationModal(false)}
              disabled={isPending || isUploading}
              className="flex-1"
            >
              {t("common:actions.cancel")}
            </Button>
          </div>
        }
      />
    </>
  );
};
